import * as React from 'react'
import { CheckCheckIcon, ChevronsUpDown, Plus, Trash } from 'lucide-react'
import { useUserStore } from '@/stores/userStore'
import { toast } from '@/hooks/use-toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { Button } from '../ui/button'
import { CreateOrganizationDialog } from './team-switcher-create-dialog'
import { deleteOrganizationById } from '@/api/organization'
import { Skeleton } from '../ui/skeleton'
import { useOrganizationSwitchActions } from '@/hooks/use-organization-switch-actions'
import { usePlan } from '@/hooks/use-plan'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { useFormmatedPlanName } from '@/hooks/use-formmated-plan-name'

export function TeamSwitcher() {
  const { t } = useTranslation();
  const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] =
    React.useState(false)
  const { userData, activeTeam, getUserData, setActiveTeam } = useUserStore(
    (state) => state.user
  )
  const { plan } = usePlan()
  const { isMobile, open } = useSidebar()
  const { executeOrganizationSwitchActions } = useOrganizationSwitchActions()
  const currentPlanType = plan?.type;
  const planName = useFormmatedPlanName(currentPlanType)

  const handleOpenDialog = () => {
    setIsCreateOrgDialogOpen(true)
  }
  const handleCloseDialog = (open: boolean) => {
    setIsCreateOrgDialogOpen(open)
  }

  const handleSwitchTeam = async (team: OrganizationDetails) => {
    try {
      await setActiveTeam(team)
      executeOrganizationSwitchActions()
      toast({ title: t(tokens.ui.toasts.organization.switch_success) })
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: t(tokens.ui.toasts.error.title),
        description: t(tokens.ui.toasts.error.generic),
      })
    }
  }

  const handleDeleteTeam = async (teamId: string) => {
    try {
      if (activeTeam?.external_id === teamId) {
        toast({ variant: 'destructive', title: t(tokens.ui.toasts.team.delete_active_error) })
        return
      }
      await deleteOrganizationById(teamId)
      getUserData()
    } catch (_error) {
      toast({ 
        variant: 'destructive', 
        title: t(tokens.ui.toasts.error.title), 
        description: t(tokens.ui.toasts.organization.delete_failed)
      })
    }
  }

  React.useEffect(() => {
    if (!userData) {
      getUserData()
    }
  }, [userData, getUserData])


  return (
    <>
      {userData ? (
        <>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size='lg'
                    className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
                  >
                    <div className='flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                      <img src={activeTeam?.logo || "/favicons/works-favicon-32x32.png"} className='size-7' />
                    </div>
                    <div className='grid flex-1 text-left text-sm leading-tight'>
                      <span className='truncate font-semibold'>
                        {activeTeam?.name}
                      </span>
                      <span className='truncate text-xs'>{t(
                        tokens.platform.teams.plan, {
                          currentPlan: planName
                        }
                      )}</span>
                    </div>
                    <ChevronsUpDown className='ml-auto' />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
                  align='start'
                  side={isMobile ? 'bottom' : 'right'}
                  sideOffset={4}
                >
                  <DropdownMenuLabel className='text-xs text-muted-foreground'>
                    {t(tokens.platform.teams.manager.title)}
                  </DropdownMenuLabel>
                  {userData?.organizations?.map((team) => (
                    <div
                      key={team.organization.external_id}
                      className='flex items-center justify-between hover:bg-accent'
                    >
                      <DropdownMenuItem
                        onClick={() => handleSwitchTeam(team.organization)}
                        className='flex-1 gap-2 p-2 hover:bg-transparent data-[highlighted]:bg-transparent'
                      >
                        <div className='border flex size-6 items-center justify-center rounded-sm'>
                          <img
                            src={team.organization.logo || "/favicons/works-favicon-32x32.png"}
                            className='size-4 shrink-0'
                          />
                        </div>
                        <span className='truncate w-28'>
                          {team.organization.name}
                        </span>
                      </DropdownMenuItem>
                      {team.organization.external_id === activeTeam?.external_id ? (
                        <Button
                          variant='ghost'
                          size='sm'
                          className='text-sidebar-foreground hover:bg-transparent focus:ring-0'
                          disabled={true}
                        >
                          <CheckCheckIcon className='size-4' />
                        </Button>
                      ) : (
                        <ConfirmDeleteDialog
                          onConfirm={() =>
                            handleDeleteTeam(team.organization.external_id)
                          }
                        >
                          <Button
                            variant='ghost'
                            size='sm'
                            className='text-sidebar-foreground hover:bg-transparent focus:ring-0'
                            disabled={userData?.organizations?.length === 1}
                          >
                            <Trash className='size-4' />
                          </Button>
                        </ConfirmDeleteDialog>
                      )}
                    </div>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className='gap-2 p-2'
                    onClick={handleOpenDialog}
                  >
                    <div className='flex size-6 items-center justify-center rounded-md border bg-background'>
                      <Plus className='size-4' />
                    </div>
                    <div className='font-medium text-muted-foreground'>
                      {t(tokens.platform.teams.add_organization.title)}
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          <CreateOrganizationDialog
            open={isCreateOrgDialogOpen}
            onOpenChange={handleCloseDialog}
          />
        </>
      ) : (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size='lg' className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
              {isMobile || open ? (
                <div className="flex items-center gap-2 w-full">
                  <Skeleton className="size-8 rounded-lg" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-[80px]" />
                  </div>
                  <Skeleton className="size-4" />
                </div>
              ) : (
                <div className="flex justify-center">
                  <Skeleton className="size-8 rounded-lg" />
                </div>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      )}
    </>
  )
}
